import { useEffect } from 'react';

/**
 * @param {boolean} params.enabled - Whether sticky scrollbar is enabled
 * @param {React.RefObject} params.scrollContainerRef - Ref to the scrollable container
 * @param {Array} params.dependencies - Dependencies that trigger scrollbar updates
 * @returns {void}
 */
export const useStickyScrollbar = ({
  enabled,
  scrollContainerRef,
  dependencies = [],
}) => {
  useEffect(() => {
    if (!enabled || !scrollContainerRef.current) return;

    const scrollContainer = scrollContainerRef.current;
    const STICKY_SCROLLBAR_ID = 'sticky-native-scrollbar';

    // Get sticky scrollbar element
    const getStickyScrollbar = () =>
      document.getElementById(STICKY_SCROLLBAR_ID);

    // Update scrollbar visibility and position
    const updateStickyScrollbar = () => {
      const { scrollWidth, clientWidth } = scrollContainer;
      const stickyScrollbar = getStickyScrollbar();

      if (!stickyScrollbar) return;

      if (scrollWidth > clientWidth) {
        const containerRect = scrollContainer.getBoundingClientRect();

        // Show and position scrollbar
        stickyScrollbar.style.display = 'block';
        stickyScrollbar.style.left = `${containerRect.left}px`;
        stickyScrollbar.style.width = `${containerRect.width}px`;

        // Set scroll width
        const scrollbarContent = stickyScrollbar.querySelector('div');
        if (scrollbarContent) {
          scrollbarContent.style.width = `${scrollWidth}px`;
        }
      } else {
        stickyScrollbar.style.display = 'none';
      }
    };

    // Event handlers
    const handleScroll = () => {
      const stickyScrollbar = getStickyScrollbar();
      if (stickyScrollbar) {
        stickyScrollbar.scrollLeft = scrollContainer.scrollLeft;
      }
    };

    const handleStickyScroll = (event) => {
      scrollContainer.scrollLeft = event.target.scrollLeft;
    };

    const handleWindowChange = () => {
      requestAnimationFrame(updateStickyScrollbar);
    };

    // Initial setup
    updateStickyScrollbar();

    // Event listeners
    const eventListeners = [
      { element: scrollContainer, event: 'scroll', handler: handleScroll },
      { element: window, event: 'scroll', handler: handleWindowChange },
      { element: window, event: 'resize', handler: handleWindowChange },
    ];

    eventListeners.forEach(({ element, event, handler }) => {
      element.addEventListener(event, handler, { passive: true });
    });

    // Sticky scrollbar listener
    const stickyScrollbarElement = getStickyScrollbar();
    if (stickyScrollbarElement) {
      stickyScrollbarElement.addEventListener('scroll', handleStickyScroll, {
        passive: true,
      });
    }

    // Resize observer
    const resizeObserver = new ResizeObserver(handleWindowChange);
    resizeObserver.observe(scrollContainer);

    // Cleanup
    return () => {
      eventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });

      if (stickyScrollbarElement) {
        stickyScrollbarElement.removeEventListener(
          'scroll',
          handleStickyScroll
        );
      }

      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies]);
};
